import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Calendar, Plus, Edit2, Trash2, ExternalLink, MapPin, Tag, Check, X } from 'lucide-react';

const CATEGORIES = ['community', 'city council', 'arts & culture', 'sport', 'environment', 'health', 'business', 'other'];
const WARDS = ['', 'ward-1', 'ward-2', 'ward-3', 'ward-4', 'ward-5', 'ward-6', 'ward-7', 'ward-8', 'ward-9', 'ward-10'];

const emptyForm = {
  title: '', description: '', event_date: '', event_time: '',
  location: '', ward: '', category: 'community', url: '', is_active: true,
};

const EventsTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // event object being edited
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/events');
      setEvents(res.data?.events || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, event_date: new Date().toISOString().split('T')[0] });
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      event_date: ev.event_date || '',
      event_time: ev.event_time || '',
      location: ev.location || '',
      ward: ev.ward || '',
      category: ev.category || 'community',
      url: ev.url || '',
      is_active: ev.is_active !== false,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.event_date) { toast.error('Title and date are required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/events/${editing.id}`, form);
        toast.success('Event updated');
      } else {
        await api.post('/admin/events', form);
        toast.success('Event created');
      }
      setShowForm(false);
      fetchEvents();
    } catch { toast.error('Failed to save event'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete event'); }
  };

  const toggleActive = async (ev) => {
    try {
      await api.put(`/admin/events/${ev.id}`, { is_active: !ev.is_active });
      fetchEvents();
    } catch { toast.error('Failed to update event'); }
  };

  const isPast = (dateStr) => dateStr < new Date().toISOString().split('T')[0];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Event Calendar</h2>
          <p className="text-gray-500 text-sm font-mono mt-1">Manage community events shown in the sidebar widget</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-[#C5A52A] hover:bg-yellow-500 text-black font-mono text-sm px-4 py-2 rounded-sm transition-colors"
          data-testid="add-event-btn"
        >
          <Plus className="w-4 h-4" /> ADD EVENT
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-white mb-4">{editing ? 'Edit Event' : 'Add New Event'}</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm" placeholder="Event name" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Date *</label>
                  <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm" required />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Time</label>
                  <input type="time" value={form.event_time} onChange={e => setForm({...form, event_time: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  rows={2} className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm resize-none" placeholder="Brief description..." />
              </div>
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Location</label>
                <input value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm" placeholder="Venue or address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Ward (optional)</label>
                  <select value={form.ward} onChange={e => setForm({...form, ward: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm">
                    <option value="">All Wards</option>
                    {WARDS.filter(Boolean).map(w => <option key={w} value={w}>{w.replace('-', ' ').toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Link (optional)</label>
                <input value={form.url} onChange={e => setForm({...form, url: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm" placeholder="https://..." />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="w-4 h-4 accent-[#C5A52A]" />
                <span className="text-sm text-gray-300">Active (visible to users)</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving}
                  className="flex-1 bg-[#C5A52A] hover:bg-yellow-500 text-black font-mono text-sm py-2 rounded disabled:opacity-50 transition-colors">
                  {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Create Event')}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-mono text-sm py-2 rounded transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events list */}
      {loading ? (
        <p className="text-gray-500 font-mono text-sm text-center py-12">Loading events...</p>
      ) : events.length === 0 ? (
        <div className="border-2 border-dashed border-gray-800 rounded p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 font-mono text-sm">No events yet. Click ADD EVENT to create the first one.</p>
          <p className="text-gray-600 font-mono text-xs mt-1">Remember to run <code className="text-yellow-600">/app/memory/events_table_setup.sql</code> in Supabase first.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(ev => {
            const past = isPast(ev.event_date);
            return (
              <div key={ev.id} className={`flex items-start gap-4 border rounded p-4 transition-colors ${past ? 'border-gray-800 bg-gray-900/50 opacity-60' : 'border-gray-700 bg-gray-900'}`}>
                {/* Date block */}
                <div className="flex-shrink-0 text-center w-14 bg-gray-800 rounded p-2">
                  <div className="text-[10px] text-gray-500 font-mono uppercase">{new Date(ev.event_date + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short' })}</div>
                  <div className="text-2xl font-black text-white leading-none">{new Date(ev.event_date + 'T12:00:00').getDate()}</div>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-white font-bold text-sm">{ev.title}</span>
                    {!ev.is_active && <span className="text-[10px] font-mono text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">HIDDEN</span>}
                    {past && <span className="text-[10px] font-mono text-gray-600 bg-gray-800 px-1.5 py-0.5 rounded">PAST</span>}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-[11px] font-mono text-gray-500">
                    {ev.event_time && <span>{ev.event_time}</span>}
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{ev.location}</span>}
                    {ev.ward && <span className="text-blue-400">{ev.ward.replace('-', ' ').toUpperCase()}</span>}
                    <span className="flex items-center gap-1"><Tag className="w-2.5 h-2.5" />{ev.category}</span>
                  </div>
                  {ev.description && <p className="text-gray-500 text-xs mt-1 truncate">{ev.description}</p>}
                </div>
                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {ev.url && (
                    <a href={ev.url} target="_blank" rel="noreferrer" className="text-gray-600 hover:text-white transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => toggleActive(ev)} title={ev.is_active ? 'Hide from users' : 'Show to users'}
                    className={`p-1 rounded transition-colors ${ev.is_active ? 'text-green-500 hover:text-gray-400' : 'text-gray-600 hover:text-green-500'}`}>
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(ev)} className="text-gray-500 hover:text-white p-1 transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(ev.id, ev.title)} className="text-gray-600 hover:text-red-500 p-1 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventsTab;
